/**
 * An inline editable list of terms with an add row that is always there.
 *
 * Terms are edited where they sit: Enter commits and moves to the next row,
 * Esc cancels, and Backspace on an emptied row removes it. The last row is
 * always an empty one, so adding is typing, not opening a form. Removal asks
 * first only when something uses the term. Pasting many at once is
 * `PasteListDialog`, which shares this list's parser.
 *
 * @module components/ui/TermList
 */

import React, {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { uid } from "@/utils/uid";

import { IconButton } from "./Button";
import { type ConfirmOptions, useConfirm } from "./ConfirmProvider";
import { TextField } from "./Form";
import { parseTerm } from "./term-list";

export interface TermListProps {
  terms: readonly string[];
  /**
   * Persists the next list. Resolves to whether it was kept: a draft stays
   * where it is when it was not, so a failed save does not eat the typing.
   */
  onChange: (next: string[]) => Promise<boolean>;
  /** What a term is, singular and lowercase: "discipline". Names the rows and the add row. */
  noun: string;
  /** How many things use each term. Absent while unknown, and nothing is counted. */
  usage?: ReadonlyMap<string, number>;
  /** Draws a row's usage in place of the default "Used by 3". */
  renderUsage?: (term: string) => ReactNode;
  /**
   * The question to ask before removing a term, or `null` to remove it
   * outright. The count is `undefined` while usage is unknown. Defaults to
   * asking unless the term is known to be unused.
   */
  confirmRemoval?: (term: string, count: number | undefined) => ConfirmOptions | null;
  /** Holds every edit while a save is out, or when the list cannot be written. */
  disabled?: boolean;
  disabledReason?: string;
  /** Test id of the add row's input. */
  addTestId?: string;
  "data-testid"?: string;
}

/** The terms last seen, with the id each row keeps for as long as it lives. */
interface Slots {
  terms: readonly string[];
  ids: readonly string[];
}

const sameTerms = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((term, index) => term === b[index]);

/**
 * Ids for a list that changed from outside. A term keeps the id at its own
 * position when the term there is unchanged, then takes the earliest unclaimed
 * previous occurrence wherever it sat, so a reorder carries a row's draft with
 * it. A term with none takes the id left at its own position when that one is
 * unclaimed (a rename), and a fresh one otherwise.
 */
function reconcile(prev: Slots, terms: readonly string[]): string[] {
  const claimed = new Set<number>();
  const matched = terms.map((term, index) => {
    if (prev.terms[index] !== term) {
      return undefined;
    }
    claimed.add(index);
    return index;
  });
  terms.forEach((term, index) => {
    if (matched[index] !== undefined) {
      return;
    }
    const at = prev.terms.findIndex((previous, from) => previous === term && !claimed.has(from));
    if (at >= 0) {
      claimed.add(at);
      matched[index] = at;
    }
  });
  return matched.map((at, index) => {
    if (at !== undefined) {
      return prev.ids[at];
    }
    return (claimed.has(index) ? undefined : prev.ids[index]) ?? uid("term");
  });
}

/**
 * One row on the list's shared columns: the term, its usage, its removal. The
 * columns are the list's, so every field is one width whatever its usage says.
 */
const ROW = "col-span-full grid grid-cols-subgrid items-center gap-x-3";

function UsageNote({
  count,
  testId,
}: {
  count: number | undefined;
  testId: string | undefined;
}): React.ReactElement | null {
  if (count === undefined) {
    return null;
  }
  return (
    <span className="text-sm text-muted" data-testid={testId}>
      {count === 0 ? "Not used" : `Used by ${String(count)}`}
    </span>
  );
}

interface RowProps {
  term: string;
  others: readonly string[];
  noun: string;
  disabled: boolean;
  disabledReason: string | undefined;
  usage: ReactNode;
  inputRef: (element: HTMLInputElement | null) => void;
  /** Resolves to whether the new value was kept. */
  onRename: (next: string) => Promise<boolean>;
  onRemove: () => void;
  onNext: () => void;
}

function TermRow({
  term,
  others,
  noun,
  disabled,
  disabledReason,
  usage,
  inputRef,
  onRename,
  onRemove,
  onNext,
}: RowProps): React.ReactElement {
  // `null` while the row only shows its term.
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState("");
  const shown = draft ?? term;

  const commit = async (): Promise<boolean> => {
    if (disabled) {
      return false;
    }
    if (draft === null) {
      return true;
    }
    const parsed = parseTerm(draft, others);
    if (parsed === null || parsed.term === term) {
      setDraft(null);
      setError("");
      return true;
    }
    if (parsed.status !== "ok") {
      setError(parsed.reason);
      return false;
    }
    const kept = await onRename(parsed.term);
    if (kept) {
      setDraft(null);
      setError("");
    }
    return kept;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      commit()
        .then((kept) => {
          if (kept) {
            onNext();
          }
        })
        .catch(() => undefined);
    } else if (event.key === "Escape" && draft !== null) {
      event.preventDefault();
      event.stopPropagation();
      setDraft(null);
      setError("");
    } else if (event.key === "Backspace" && shown === "") {
      event.preventDefault();
      onRemove();
    }
  };

  const inputId = useId();
  const errorId = `${inputId}-error`;
  return (
    <li className={ROW}>
      <TextField
        id={inputId}
        value={shown}
        onChange={(value) => {
          setDraft(value);
          setError("");
        }}
        onBlur={() => {
          commit().catch(() => undefined);
        }}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        size="sm"
        aria-label={`Edit ${noun} ${term}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        disabled={disabled}
        disabledReason={disabledReason}
      />
      {/* Under the term on a narrow list, so the term keeps the width; beside it once there is room. */}
      <span className="order-last col-start-1 px-3 @md:order-none @md:col-start-auto @md:px-0 @md:text-end">
        {usage}
      </span>
      <IconButton
        icon="trash"
        size="sm"
        disabled={disabled}
        disabledReason={disabledReason}
        aria-label={`Remove ${term}`}
        onClick={onRemove}
      />
      {error ? (
        <p id={errorId} className="order-last col-span-full px-3 pb-1 text-sm text-error-strong">
          {error}
        </p>
      ) : null}
    </li>
  );
}

/**
 * @example
 * ```tsx
 * <TermList terms={disciplines} noun="discipline" onChange={save} usage={usage} />
 * ```
 */
export function TermList({
  terms,
  onChange,
  noun,
  usage = undefined,
  renderUsage = undefined,
  confirmRemoval = undefined,
  disabled = false,
  disabledReason = undefined,
  addTestId = undefined,
  "data-testid": testId,
}: TermListProps): React.ReactElement {
  const confirm = useConfirm();
  const addId = useId();
  const addErrorId = `${addId}-error`;
  const rows = useRef<(HTMLInputElement | null)[]>([]);
  const addRow = useRef<HTMLInputElement | null>(null);
  const [entry, setEntry] = useState("");
  const [addError, setAddError] = useState("");
  const [known, setKnown] = useState<Slots>(() => ({ terms, ids: terms.map(() => uid("term")) }));
  // What a local edit expects the list to become, so its rows keep their ids
  // when the parent hands the result back.
  const [expected, setExpected] = useState<Slots | null>(null);
  let slots = known;
  if (!sameTerms(known.terms, terms)) {
    slots = {
      terms,
      ids: expected && sameTerms(expected.terms, terms) ? expected.ids : reconcile(known, terms),
    };
    setKnown(slots);
    setExpected(null);
  }

  // What a dialog that opened earlier reads when it is answered, since the
  // list may have changed while it was open.
  const latest = useRef({ terms, ids: slots.ids, onChange });
  useEffect(() => {
    latest.current = { terms, ids: slots.ids, onChange };
  });

  const change = (nextTerms: string[], nextIds: readonly string[]): Promise<boolean> => {
    setExpected({ terms: nextTerms, ids: nextIds });
    return latest.current.onChange(nextTerms);
  };

  const focusRow = (index: number): void => {
    // After the render that follows a change, so the row it lands on exists.
    setTimeout(() => {
      (rows.current[index] ?? addRow.current)?.focus();
    }, 0);
  };

  const remove = (term: string, index: number): void => {
    if (disabled) {
      return;
    }
    const count = usage ? (usage.get(term) ?? 0) : undefined;
    let options: ConfirmOptions | null = null;
    if (confirmRemoval) {
      options = confirmRemoval(term, count);
    } else if (count !== 0) {
      options = {
        title: `Remove ${term}?`,
        body:
          count === undefined
            ? "Whether anything uses it could not be checked."
            : `It is used by ${String(count)}. Removing it does not change anything they already have.`,
        confirmLabel: `Remove ${noun}`,
        tone: "danger",
      };
    }
    const rowId = slots.ids[index];
    const proceed = options ? confirm(options) : Promise.resolve(true);
    proceed
      .then(async (confirmed) => {
        if (!confirmed) {
          return;
        }
        const now = latest.current;
        const at = now.ids.indexOf(rowId);
        // Gone, or renamed to something the dialog did not ask about.
        if (at < 0 || now.terms[at] !== term) {
          return;
        }
        const kept = await change(
          now.terms.filter((_, position) => position !== at),
          now.ids.filter((_, position) => position !== at),
        );
        if (kept) {
          focusRow(Math.max(0, at - 1));
        }
      })
      .catch(() => undefined);
  };

  const add = (): void => {
    if (disabled) {
      return;
    }
    const parsed = parseTerm(entry, terms);
    if (parsed === null) {
      return;
    }
    if (parsed.status !== "ok") {
      setAddError(parsed.reason);
      return;
    }
    change([...terms, parsed.term], [...slots.ids, uid("term")])
      .then((kept) => {
        if (kept) {
          // Only if untouched since: typing past a slow save is not lost.
          setEntry((current) => (parseTerm(current)?.term === parsed.term ? "" : current));
          setAddError("");
        }
      })
      .catch(() => undefined);
  };

  const handleAddKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      add();
    } else if (event.key === "Escape" && entry !== "") {
      event.preventDefault();
      event.stopPropagation();
      setEntry("");
      setAddError("");
    }
  };

  return (
    <div className="@container">
      <ul
        className="grid grid-cols-[minmax(0,1fr)_auto] gap-y-2 @md:grid-cols-[minmax(0,1fr)_auto_auto]"
        data-testid={testId}
      >
        {terms.map((term, index) => (
          <TermRow
            // Not the term or the position: a rename changes the first and a
            // removal shifts the second, and either would remount or mix up rows.
            key={slots.ids[index]}
            term={term}
            others={terms.filter((_, at) => at !== index)}
            noun={noun}
            disabled={disabled}
            disabledReason={disabledReason}
            usage={
              renderUsage ? (
                renderUsage(term)
              ) : (
                <UsageNote count={usage?.get(term)} testId={undefined} />
              )
            }
            inputRef={(element) => {
              rows.current[index] = element;
            }}
            onRename={(next) =>
              change(
                terms.map((existing, at) => (at === index ? next : existing)),
                slots.ids,
              )
            }
            onRemove={() => {
              remove(term, index);
            }}
            onNext={() => {
              focusRow(index + 1);
            }}
          />
        ))}
        <li className={ROW}>
          <TextField
            id={addId}
            value={entry}
            onChange={(value) => {
              setEntry(value);
              setAddError("");
            }}
            onKeyDown={handleAddKeyDown}
            inputRef={addRow}
            size="sm"
            placeholder={`Add a ${noun}`}
            aria-label={`Add a ${noun}`}
            aria-invalid={addError ? true : undefined}
            aria-describedby={addError ? addErrorId : undefined}
            disabled={disabled}
            disabledReason={disabledReason}
            data-testid={addTestId}
          />
          {addError ? (
            <p id={addErrorId} className="col-span-full px-3 pb-1 text-sm text-error-strong">
              {addError}
            </p>
          ) : null}
        </li>
      </ul>
    </div>
  );
}
